import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import BackNav from '../../components/navigation/BackNav';
import LearnPageShell from './LearnPageShell';
import NotebookAvatar from './NotebookAvatar';
import LearnNotebookSharingPanel from './LearnNotebookSharingPanel';
import {
  NOTEBOOK_ACCESS,
  applyNotebookSharing,
  defaultAccessModeForPlan,
  resolveAccessModeForPlan,
  visibilityFromAccessMode,
} from '../../utils/notebookSharing';
import './learn.css';

const STEPS = ['Basics', 'Details', 'Sharing', 'Review'];
const NOTEBOOK_COVER = '📓';

function accessSummaryLabel(mode, email) {
  if (mode === NOTEBOOK_ACCESS.PUBLIC) return 'Public — anyone can discover';
  if (mode === NOTEBOOK_ACCESS.TEAM) return 'Shared with your team';
  if (mode === NOTEBOOK_ACCESS.EMAIL && email.trim()) return `Invite: ${email.trim()}`;
  return 'Private — only you';
}

export default function LearnNotebookCreatePage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [moduleLabel, setModuleLabel] = useState('');
  const [accessMode, setAccessMode] = useState(NOTEBOOK_ACCESS.PUBLIC);
  const [memberEmail, setMemberEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [plan, setPlan] = useState('FREE');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/api/users/me/usage');
        if (!cancelled) {
          const nextPlan = (res.data?.plan || 'FREE').toUpperCase();
          setPlan(nextPlan);
          setAccessMode(defaultAccessModeForPlan(nextPlan));
        }
      } catch {
        if (!cancelled) setPlan('FREE');
      }
    })();
    return () => { cancelled = true; };
  }, [apiClient]);

  const goNext = () => {
    setError('');
    setEmailError('');
    if (step === 0 && !title.trim()) {
      setError('Add a notebook title to continue.');
      return;
    }
    if (step === 2 && accessMode === NOTEBOOK_ACCESS.EMAIL) {
      const email = memberEmail.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailError('Enter a valid email address to invite someone.');
        return;
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError('');
    setEmailError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Notebook name is required.');
      setStep(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const mode = resolveAccessModeForPlan(plan, accessMode);
      const visibility = visibilityFromAccessMode(mode);
      const res = await apiClient.post('/api/v1/learn/notebooks', {
        title: title.trim(),
        description: description.trim(),
        moduleLabel: moduleLabel.trim(),
        coverIcon: NOTEBOOK_COVER,
        visibility,
        difficulty: 'INTERMEDIATE',
      });
      const notebookId = res.data.id;
      if (mode === NOTEBOOK_ACCESS.TEAM || mode === NOTEBOOK_ACCESS.EMAIL) {
        await applyNotebookSharing(apiClient, notebookId, mode, memberEmail);
      }
      navigate(`/hub/notebooks/${notebookId}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not create notebook.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta title="Create notebook" description="Create a personal QnaRead notebook" />
      <LearnPageShell userProfile={userProfile}>
        <BackNav to="/hub" label="Learn" />
        <section className="sh-block learn-create-block">
          <header className="sh-block-head learn-create-head">
            <div className="sh-block-head-main">
              <NotebookAvatar size="lg" />
              <div className="learn-create-head-text">
                <p className="learn-page-eyebrow">New notebook</p>
                <h1 className="sh-block-title learn-create-title">Create notebook</h1>
                <p className="sh-block-sub learn-create-step-caption">
                  Step {step + 1} of {STEPS.length}: {STEPS[step]}
                </p>
              </div>
            </div>
          </header>

          <ol className="learn-create-stepper learn-create-stepper--4" aria-label="Create notebook steps">
            {STEPS.map((label, i) => (
              <li
                key={label}
                className={`learn-create-stepper-item${i === step ? ' is-active' : ''}${i < step ? ' is-done' : ''}`}
                aria-current={i === step ? 'step' : undefined}
              >
                <span className="learn-create-stepper-num">{i + 1}</span>
                <span className="learn-create-stepper-label">{label}</span>
              </li>
            ))}
          </ol>

          <form
            className={`learn-create-form learn-form-panel${step === 2 ? ' learn-form-panel--full' : ''}`}
            onSubmit={(e) => {
              e.preventDefault();
              if (step < STEPS.length - 1) goNext();
              else submit(e);
            }}
          >
            {error && <p className="learn-inline-alert" role="alert">{error}</p>}

            {step === 0 && (
              <div className="learn-form-fields">
                <div className="learn-form-field">
                  <label htmlFor="nb-title">Title</label>
                  <input
                    id="nb-title"
                    className="learn-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Operating Systems"
                    autoFocus
                  />
                  <p className="learn-field-hint">Required — shown on your notebook card.</p>
                </div>
                <div className="learn-form-field">
                  <label htmlFor="nb-mod">Subject / module</label>
                  <input
                    id="nb-mod"
                    className="learn-input"
                    value={moduleLabel}
                    onChange={(e) => setModuleLabel(e.target.value)}
                    placeholder="e.g. Computer Science"
                  />
                  <p className="learn-field-hint">Optional — helps you organize several notebooks.</p>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="learn-form-fields">
                <div className="learn-form-field">
                  <label htmlFor="nb-desc">Description</label>
                  <textarea
                    id="nb-desc"
                    className="learn-input learn-textarea"
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Short summary for your reference (optional)"
                    autoFocus
                  />
                  <p className="learn-field-hint">Optional — appears on the notebook card when set.</p>
                </div>
              </div>
            )}

            {step === 2 && (
              <LearnNotebookSharingPanel
                accessMode={accessMode}
                onAccessModeChange={setAccessMode}
                memberEmail={memberEmail}
                onMemberEmailChange={setMemberEmail}
                emailError={emailError}
                compact={false}
                emailInputId="nb-invite-email-create"
              />
            )}

            {step === 3 && (
              <div className="learn-create-review">
                <div className="learn-create-review-row">
                  <NotebookAvatar size="md" />
                  <div className="learn-create-review-body">
                    <p className="learn-create-review-title">{title.trim() || 'Untitled notebook'}</p>
                    {moduleLabel.trim() ? (
                      <span className="learn-nb-card-module">{moduleLabel.trim()}</span>
                    ) : null}
                    {description.trim() ? (
                      <p className="learn-nb-card-desc">{description.trim()}</p>
                    ) : (
                      <p className="learn-muted">No description</p>
                    )}
                    <p className="learn-create-review-access">
                      <strong>Access:</strong> {accessSummaryLabel(accessMode, memberEmail)}
                    </p>
                  </div>
                </div>
                <p className="learn-create-review-hint">
                  After you create this notebook, use <strong>Import PDF</strong> on the notebook page to generate Q&amp;A.
                </p>
              </div>
            )}

            <div className="learn-form-actions learn-create-actions">
              <button type="button" className="home-btn home-btn-secondary" onClick={() => navigate('/hub')}>
                Cancel
              </button>
              {step > 0 && (
                <button type="button" className="home-btn home-btn-secondary" onClick={goBack}>
                  Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button type="button" className="home-btn home-btn-primary" onClick={goNext}>
                  Next
                </button>
              ) : (
                <button type="submit" className="home-btn home-btn-primary" disabled={loading}>
                  {loading ? 'Creating…' : 'Create notebook'}
                </button>
              )}
            </div>
          </form>
        </section>
      </LearnPageShell>
    </>
  );
}
